import { useEffect, useImperativeHandle, useRef } from 'react';
import type { ForwardedRef } from 'react';
import type React from 'react';
import _ from 'lodash';
import { useMergeState } from '@/shared/lib/customHooks';
import type { SearchInputHandle, SearchInputProps } from './searchInput';

/**
 * Wraps a typed token into the shape the creatable select expects for multi-value search. The same text becomes both the visible label and the stored value.
 *
 * @param label - The text typed by the user.
 * @returns An option carrying label and value set to the same text.
 */
const createOption = (label: string) => ({
  label,
  value: label,
});

/**
 * Backs a search field that can behave as a plain text input, a numeric input or a creatable multi-token select. Merge state holds the current value and the in-progress token, an imperative handle exposes focus to the parent, numeric mode rejects non-digit input, space and enter commit the typed token as a new option, and an effect stops keydown propagation from the element with the given id.
 *
 * @param props - Search input configuration including id, name, onChange, numeric and multi-token settings.
 * @param ref - Forwarded ref receiving the focus handle.
 * @returns An object with textInputRef, state and the change, clear, select and keydown handlers.
 */
const useSearchInput = (
  {
    id,
    name,
    onChange,
    onChangeMultiSearchValue,
    isNumberOnly = false,
    multiSearchValue = [],
    shouldPreventFromInput = false,
  }: Pick<
    SearchInputProps,
    | 'id'
    | 'name'
    | 'onChange'
    | 'onChangeMultiSearchValue'
    | 'isNumberOnly'
    | 'multiSearchValue'
    | 'shouldPreventFromInput'
  >,
  ref: ForwardedRef<SearchInputHandle>,
) => {
  const textInputRef = useRef<HTMLInputElement>(null);

  const [state, setState] = useMergeState<{ value: any[]; inputValue: string }>({
    value: [],
    inputValue: '',
  });

  useImperativeHandle(ref, () => ({
    handleFocusInput: () => {
      textInputRef.current?.focus();
    },
  }));

  /**
   * Relays typed search text to the owning form, rejecting non-numeric input where the field is restricted to digits. A regex test guards the numeric case and only a passing value reaches the change handler under the field name.
   *
   * @param e - Change event from the text input.
   * @returns Nothing.
   */
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    const isNumber = /^\d+$/.test(value);
    if (isNumberOnly && shouldPreventFromInput && value !== '' && !isNumber) {
      return;
    }
    onChange(name, value);
  };

  /**
   * Relays a value emitted by a wrapped control that reports an object rather than a DOM event. The inner value is destructured out and sent to the form change handler under the field name.
   *
   * @param value - The new value carried by the control's payload.
   * @returns Nothing.
   */
  const onValueChange = ({ value }: { value: string }) => {
    onChange(name, value);
  };

  /**
   * Forwards a change coming from a sibling selection control rather than the search box itself. The caller supplies its own field name, which is passed through to the form change handler unchanged.
   *
   * @param selectionName - Field name of the selection control that changed.
   * @param value - The newly selected value.
   * @returns Nothing.
   */
  const onChangeSelection = (selectionName: string, value: any) => {
    onChange(selectionName, value);
  };

  /**
   * Empties the search field when the clear affordance is pressed. An empty string is sent to the form change handler under the field name.
   *
   * @returns Nothing.
   */
  const onClickClearSearch = () => {
    onChange(name, '');
  };

  /**
   * Stores the tokens currently selected in the creatable search select. Only local merge state is written, so the owning form is not notified here.
   *
   * @param value - The new selection for the creatable select.
   * @returns Nothing.
   */
  const onChangeCreatableSelect = (value: any) => {
    setState({ value });
  };

  /**
   * Tracks the token text being typed into the creatable search select. Non-empty input that does not parse as an integer is discarded while the field is in numeric-only mode, otherwise the text is written to merge state.
   *
   * @param inputValue - Current text in the select's input box.
   * @returns Nothing.
   */
  const onInputChangeCreatableSelect = (inputValue: string) => {
    if (isNumberOnly && inputValue !== '' && _.isNaN(parseInt(inputValue, 10))) {
      return;
    }
    setState({ inputValue });
  };

  /**
   * Commits the in-progress text as a search token on space or enter. Space only adds the token when no existing token carries the same value, enter always adds it, both clear the input text and suppress the default key behaviour, and any other key with empty input is ignored.
   *
   * @param e - Keyboard event from the creatable select input.
   * @returns Nothing.
   */
  const onKeyDownCreatableSelect = (e: React.KeyboardEvent) => {
    if (!state.inputValue) {
      return;
    }

    switch (e.keyCode) {
      case 32: {
        if (!_.find(multiSearchValue, (x) => x.value === state.inputValue)) {
          onChangeMultiSearchValue(name, createOption(state.inputValue));
          e.preventDefault();
        }
        setState({ inputValue: '' });
        break;
      }
      case 13: {
        onChangeMultiSearchValue(name, createOption(state.inputValue));
        setState({ inputValue: '' });
        e.preventDefault();
        break;
      }
      default: {
        break;
      }
    }
  };

  useEffect(() => {
    if (id) {
      const input = document.getElementById(id);
      if (input) {
        input.addEventListener('keydown', (event) => {
          event.stopPropagation();
        });
      }
    }
  }, [id]);

  return {
    textInputRef,
    state,
    handleChange,
    onValueChange,
    onChangeSelection,
    onClickClearSearch,
    onChangeCreatableSelect,
    onInputChangeCreatableSelect,
    onKeyDownCreatableSelect,
  };
};

export default useSearchInput;
